"use client";

import { cn } from "@gettactic/helpers";
import {
  formatFileSize,
  getFileExtension,
  getFileKind,
  isImageFile
} from "@gettactic/helpers/src/files/fileType";
import { AlertCircle, Loader2, RotateCw, X } from "lucide-react";
import React from "react";
import { Image } from "../../data-display/Image/Image";
import type { AttachmentTileProps } from "./AttachmentTile.types";
import { getFileKindIcon } from "./fileKindIcon";

const SQUARE_SIZE = {
  sm: "h-16 w-16",
  md: "h-24 w-24"
} as const;

const CHIP_SIZE = {
  sm: "h-16 max-w-[220px]",
  md: "h-24 max-w-[260px]"
} as const;

const CHIP_ICON_SIZE = {
  sm: "h-9 w-9",
  md: "h-12 w-12"
} as const;

/**
 * One attached file, as a fixed-height card: images become a thumbnail square,
 * everything else a chip with its type icon, name and size. Used both while
 * composing (with `onRemove` and an upload overlay) and on a posted comment.
 *
 * Three ways to act on it, in order: retry a failed upload, open it in-app via
 * `onOpen`, or follow `url` out to the stored file.
 */
export const AttachmentTile: React.FC<AttachmentTileProps> = ({
  filename,
  url,
  byteSize,
  status = "ready",
  errorMessage,
  size = "sm",
  onRemove,
  onRetry,
  onOpen,
  className
}) => {
  const label = filename || "Attachment";
  const extension = getFileExtension(filename, url);
  const isImage = isImageFile(filename, url);
  const KindIcon = getFileKindIcon(getFileKind(filename, url));

  const isUploading = status === "uploading";
  const hasFailed = status === "error";
  const canRetry = hasFailed && Boolean(onRetry);
  // An in-app preview reads off a local object URL, so it works before the
  // upload has finished; only a failed tile trades it for the retry action.
  const canOpen = !hasFailed && Boolean(onOpen);
  // Linking out is different: it points at the stored file, which only exists
  // once the upload succeeded.
  const canFollowLink = status === "ready" && Boolean(url) && !onOpen;
  const isInteractive = canOpen || canFollowLink || canRetry;

  const meta = [
    extension.toUpperCase(),
    byteSize ? formatFileSize(byteSize) : ""
  ]
    .filter(Boolean)
    .join(" · ");

  const surface = cn(
    "flex items-center overflow-hidden rounded-brand border bg-white transition-colors",
    hasFailed ? "border-red-300 bg-red-50/60" : "border-neutral-200",
    isInteractive && !hasFailed && "group-hover/tile:border-neutral-300"
  );

  const body = isImage ? (
    <span
      className={cn(
        surface,
        "justify-center bg-neutral-100",
        SQUARE_SIZE[size]
      )}
    >
      {url ? (
        <Image src={url} alt={label} wrapperClassName="h-full w-full" />
      ) : (
        <KindIcon className="h-5 w-5 text-neutral-400" />
      )}
    </span>
  ) : (
    <span className={cn(surface, "gap-2.5 px-2.5", CHIP_SIZE[size])}>
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md bg-neutral-100",
          CHIP_ICON_SIZE[size]
        )}
      >
        <KindIcon className="h-4 w-4 text-neutral-500" />
      </span>
      <span className="flex min-w-0 flex-col text-left">
        <span className="truncate font-medium text-neutral-900 text-xs">
          {label}
        </span>
        {meta ? (
          <span className="truncate text-2xs text-neutral-500">{meta}</span>
        ) : null}
      </span>
    </span>
  );

  const overlay = (
    <>
      {isUploading ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-brand bg-white/70">
          <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />
        </span>
      ) : null}
      {hasFailed ? (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-brand bg-red-50/70">
          {canRetry ? (
            <RotateCw className="h-4 w-4 text-red-500" />
          ) : (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
        </span>
      ) : null}
    </>
  );

  const failureTitle = errorMessage
    ? `${label} — ${errorMessage}${canRetry ? ". Click to try again" : ""}`
    : label;
  const title = hasFailed ? failureTitle : label;

  const content = canRetry ? (
    <button
      type="button"
      onClick={onRetry}
      title={title}
      className="block rounded-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {body}
      <span className="sr-only">Retry uploading {label}</span>
    </button>
  ) : onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      disabled={!canOpen}
      title={title}
      className="block rounded-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {body}
      <span className="sr-only">Open {label}</span>
    </button>
  ) : canFollowLink && url ? (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={title}
      className="block rounded-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      {body}
    </a>
  ) : (
    <span title={title} className="block">
      {body}
    </span>
  );

  return (
    <span className={cn("group/tile relative inline-block", className)}>
      {content}
      {overlay}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          className={cn(
            "absolute -top-1.5 -right-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-neutral-900 text-white opacity-0 ring-2 ring-white transition-opacity",
            "hover:bg-neutral-700 focus-visible:opacity-100 focus-visible:outline-none group-hover/tile:opacity-100",
            // Hover isn't available on touch, so the control stays visible there.
            "max-sm:opacity-100"
          )}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
};
